import { useCallback, useEffect, useRef } from 'react';

/**
 * Reports an element's rendered height (border-box px) to `onHeight`, then again
 * on every resize, and reports 0 once the element unmounts.
 *
 * Returns a ref CALLBACK rather than a ref object on purpose: callers observe a
 * conditionally rendered node, and a `[]`-dep effect would latch onto the first
 * mount and keep measuring a detached element after a remount (the LR-518 bug
 * on AccrualChart). A ref callback re-wires on every attach/detach.
 *
 * jsdom stubs ResizeObserver to a no-op and reports 0-height rects, so tests get
 * the initial 0 and callers must keep a static fallback for the unmeasured case.
 */
export const useElementHeight = (
  onHeight?: (px: number) => void
): ((node: HTMLElement | null) => void) => {
  const report = useRef(onHeight);
  const observer = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    report.current = onHeight;
  }, [onHeight]);

  useEffect(() => () => observer.current?.disconnect(), []);

  return useCallback((node: HTMLElement | null) => {
    observer.current?.disconnect();
    observer.current = null;

    if (!node) {
      report.current?.(0);
      return;
    }

    report.current?.(node.getBoundingClientRect().height);
    if (typeof ResizeObserver === 'undefined') return;
    observer.current = new ResizeObserver(() =>
      report.current?.(node.getBoundingClientRect().height)
    );
    observer.current.observe(node);
  }, []);
};
